import * as React from 'react';

// Material-UI
import BlockIcon from '@mui/icons-material/Block';
import Box from '@mui/material/Box';
import { amber, blueGrey, green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Divider from '@mui/material/Divider';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Grid from '@mui/material/Grid';
import PanToolIcon from '@mui/icons-material/PanTool';
import PendingIcon from '@mui/icons-material/Pending';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import RequestSummaryGuests from './RequestSummaryGuests';
import RequestSummaryMembers from './RequestSummaryMembers';
import RequestSummaryPasses from './RequestSummaryPasses';

import {
  HTTPHeaders,
} from "../../interfaces";

import {
  RequestSummary
} from '../../models';

import {
  API_URL,
  EMPTY_STRING,
  NULL_ID,
  SignupStatusID
} from '../../global';

// UTC to local date formatting hooks
import { useDates } from '../../hooks/UseDates';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

interface RequestSummaryInfoProps {
  id: number;
}

const RequestSummaryInfo: React.FC<RequestSummaryInfoProps> = (props) => {

  const { getAccessToken } = useAuth0User();
  const { dateStringToLocalDateFormatted } = useDates();

  const [summary, setSummary] = React.useState<RequestSummary>(new RequestSummary());

  // #region Styles

  const theme = useTheme();
  const styles = {
    confirmed: {
      color: green[500]
    },
    deined: {
      color: red[500]
    },
    divider: {
      margin: theme.spacing(1, 0, 1, 0)
    },
    pending: {
      color: blueGrey[500]
    },
    rowSpacing: {
      margin: theme.spacing(1, 0, 0, 0)
    },
    title: {
      margin: theme.spacing(0, 0, 1, 0)
    },
    waitlist: {
      color: amber[500]
    }
  };

  // #endregion

  // #region Methods

  /*
* Fetch request summary
*/
  const fetchSummary = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always show current date forward
    const params = new URLSearchParams();
    params.append('id', props.id.toString());

    const url = new URL(`${API_URL}/v1/requests/requestsummary`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Request summary not retrieved');
        }
        return response.json()
      })
      .then((summary: RequestSummary) => {
        setSummary(summary);
      })
      .catch(err => {
        throw new Error('Request summary not retrieved');
      });
  }

  /*
* Render the request status icon
*/
  const renderStatus = (statusID: number, status: string) => {

    switch (statusID) {
      case SignupStatusID.Requested:
        return (
          <Tooltip title={status} arrow>
            <PendingIcon fontSize="medium" sx={styles.pending} />
          </Tooltip>
        )
      case SignupStatusID.Confirmed:
        return (
          <Tooltip title={status} arrow>
            <CheckCircleIcon fontSize="medium" sx={styles.confirmed} />
          </Tooltip>
        )
      case SignupStatusID.Waitlist:
        return (
          <Tooltip title={status} arrow>
            <PanToolIcon fontSize="medium" sx={styles.waitlist} />
          </Tooltip>
        )
      case SignupStatusID.Denied:
        return (
          <Tooltip title={status} arrow>
            <BlockIcon fontSize="medium" sx={styles.deined} />
          </Tooltip>
        )
      case SignupStatusID.Mixed:
        return (
          <Tooltip title={status} arrow>
            <DragIndicatorIcon fontSize="medium" />
          </Tooltip>
        )
      default:
        return (<span />)
    }

  }

  // #endregion

  // #region Event Handlers

  React.useEffect(() => {
    if (props.id !== NULL_ID) {
      fetchSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id]);

  // #endregion

  return (
    <Box>
      <Grid container>
        <Grid container size={12} spacing={2}>
          <Grid>
            <Typography variant="button">{summary.referenceNumber}</Typography>
          </Grid>
          <Grid>
            {renderStatus(summary.statusID, summary.status)}
          </Grid>
          <Grid>
            <Typography variant="button">{dateStringToLocalDateFormatted(summary.minArrivalDate, 'EEEEEE MM/dd/yyyy')} - {dateStringToLocalDateFormatted(summary.maxDepartureDate, 'EEEEEE MM/dd/yyyy')}</Typography>
          </Grid>
          <Grid>
            <Typography variant="button">Nights: {summary.nights}</Typography>
          </Grid>
        </Grid>
        {summary.notes !== EMPTY_STRING &&
          <Grid container size={12}><Typography variant="body2"><i>Notes: {summary.notes}</i></Typography></Grid>
        }
      </Grid>
      <Divider sx={styles.divider} />
        {summary.members.length > 0 &&
          <RequestSummaryMembers
            members={summary.members}
          />
        }
        {summary.guests.length > 0 &&
          <RequestSummaryGuests
            guests={summary.guests}
          />
        }
        {summary.passes.length > 0 &&
          <RequestSummaryPasses
            passes={summary.passes}
          />
        }
    </Box>
  );

}

export default RequestSummaryInfo;